
import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthSessionService } from '../../services/auth-session/auth-session.service';

import {
  OrderItemModel,
  OrderModel
} from '../../core/models';

import { AdminService } from '../../services/admin/admin.service';
import { InvoiceService } from '../../services/invoice/invoice.service';


@Component({
  selector: 'app-admin-orders',
  templateUrl: './admin-orders.component.html',
  styleUrls: ['./admin-orders.component.scss']
})
export class AdminOrdersComponent implements OnInit {


  // =========================================================
  // Order List
  // =========================================================

  rows: OrderModel[] = [];


  // =========================================================
  // Order Details Modal
  // =========================================================

  selectedOrder: OrderModel | null = null;

  showOrderDetails: boolean = false;


  // =========================================================
  // Pagination
  // =========================================================

  currentPage: number = 1;

  pageSize: number = 10;


  // =========================================================
  // Constructor
  // =========================================================

  constructor(
    private adminService: AdminService,
    private invoiceService: InvoiceService,
    private authSession: AuthSessionService
  ) { }


  // =========================================================
  // Component Init
  // =========================================================

  ngOnInit(): void {

    this.loadPageData();

  }


  // =========================================================
  // Load Orders
  // =========================================================

  loadPageData(): void {
    const token = this.authSession.getCurrentUser()?.accessToken || '';
    forkJoin({
      legacy: this.adminService.getOrders().pipe(catchError(() => of([] as OrderModel[]))),
      unified: token ? this.adminService.getUnifiedCartOrders(token).pipe(catchError(() => of([] as OrderModel[]))) : of([] as OrderModel[])
    }).subscribe(({legacy, unified}) => {
      this.rows = [...unified, ...legacy].sort((a,b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      this.validateCurrentPage();
    });
  }

  // =========================================================
  // View Order Details
  // =========================================================

  viewOrderDetails(order: OrderModel): void {

    this.selectedOrder = order;

    this.showOrderDetails = true;

  }


  // =========================================================
  // Download Invoice
  // =========================================================

  downloadInvoice(order: OrderModel): void {

    this.invoiceService.downloadInvoice(
      this.invoiceService.buildFromOrder(order)
    );

  }


  // =========================================================
  // Get Order Items
  // =========================================================

  getOrderItems(
    order: OrderModel | null
  ): OrderItemModel[] {

    if (!order) {

      return [];

    }

    return order.items?.length
      ? order.items
      : [
        {
          courseId: 0,
          courseName: 'Course booking',
          quantity: 1,
          unitPrice: Number(order.totalAmount || 0),
          lineAmount: Number(order.totalAmount || 0)
        }
      ];

  }


  // =========================================================
  // Pagination
  // =========================================================

  // Get current page orders
  get pagedOrders(): OrderModel[] {

    const startIndex =
      (this.currentPage - 1) * this.pageSize;

    return this.rows.slice(
      startIndex,
      startIndex + this.pageSize
    );

  }


  // Get total pages
  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(
        this.rows.length / this.pageSize
      )
    );

  }


  // Get page numbers
  get pageNumbers(): number[] {

    return Array.from(
      { length: this.totalPages },
      (_, index) => index + 1
    );

  }


  // Get first displayed record
  get startRecord(): number {

    if (this.rows.length === 0) {

      return 0;

    }

    return (
      (this.currentPage - 1) *
      this.pageSize
    ) + 1;

  }


  // Get last displayed record
  get endRecord(): number {

    return Math.min(
      this.currentPage * this.pageSize,
      this.rows.length
    );

  }


  // Change current page
  changePage(page: number): void {

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > this.totalPages
    ) {

      return;

    }

    this.currentPage = page;

  }


  // Previous page
  previousPage(): void {

    this.changePage(
      this.currentPage - 1
    );

  }


  // Next page
  nextPage(): void {

    this.changePage(
      this.currentPage + 1
    );

  }


  // Change rows per page
  onPageSizeChange(): void {

    this.currentPage = 1;

  }


  // Validate page after loading records
  private validateCurrentPage(): void {

    this.currentPage = Math.max(
      1,
      Math.min(
        this.currentPage,
        this.totalPages
      )
    );

  }

}
